/** Small presentation helpers for the dbm. chat workspace. */

export function initials(name: string | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Human-readable attachment size. Shared by the message rows, the global
 * Files rail panel, and the per-conversation Files list. */
export function formatFileSize(bytes?: number): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * A readable company name for a domain nobody has named yet:
 * "discountbuildingmaterial.com.au" → "Discountbuildingmaterial",
 * "rear-build.com" → "Rear Build".
 *
 * Takes the first label rather than trying to strip suffixes, which
 * avoids maintaining a list of them (.com.au, .co.uk, .io, …). A company
 * name set in the admin panel always wins over this — it's only here so
 * an unsorted domain still reads like a company instead of a URL.
 */
export function companyFromDomain(domain: string | undefined): string {
  const first = (domain || "").split(".")[0] || "";
  if (!first) return "Unknown";
  return first
    .split(/[-_]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

const AVATAR_COLOR_COUNT = 8;

/**
 * Deterministic per-user avatar color class (av-c0..av-c7, defined in
 * dbm-workspace.css) — same seed (uid, or name as fallback) always maps
 * to the same swatch, so a person is visually recognisable at a glance
 * across the whole app without every avatar being the same brand orange.
 */
export function avatarColorClass(seed: string | undefined): string {
  const s = seed || "?";
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = (hash * 31 + s.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(hash) % AVATAR_COLOR_COUNT;
  return `av-c${idx}`;
}

/** Short HH:MM in 24h, from a CometChat epoch-seconds timestamp. */
export function shortTime(sentAtSeconds: number | undefined): string {
  if (!sentAtSeconds) return "";
  const d = new Date(sentAtSeconds * 1000);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

/** Seconds → "45s" / "3m 12s" / "1h 05m" — used for call durations. */
export function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, "0")}s`;
  return `${sec}s`;
}

/** e.g. "MON 06 JUL" — the divider label style from the design. */
export function dayLabel(sentAtSeconds: number | undefined): string {
  const d = sentAtSeconds ? new Date(sentAtSeconds * 1000) : new Date();
  const day = d.toLocaleDateString([], { weekday: "short" }).toUpperCase();
  const date = String(d.getDate()).padStart(2, "0");
  const mon = d.toLocaleDateString([], { month: "short" }).toUpperCase();
  return `${day} ${date} ${mon}`;
}

/** YYYY-MM-DD key for grouping messages into day dividers. */
export function dayKey(sentAtSeconds: number | undefined): string {
  const d = sentAtSeconds ? new Date(sentAtSeconds * 1000) : new Date();
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** The current top-bar clock string, e.g. "MON 06 JUL · 07:42". */
export function clockLabel(now: Date = new Date()): string {
  const day = now.toLocaleDateString([], { weekday: "short" }).toUpperCase();
  const date = String(now.getDate()).padStart(2, "0");
  const mon = now.toLocaleDateString([], { month: "short" }).toUpperCase();
  const time = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true }).toUpperCase();
  return `${day} ${date} ${mon} · ${time}`;
}

/**
 * Reads an org tag (SUPPLIER / CUSTOMER / STAFF) off a CometChat user's
 * metadata. Per the brief, partner org type lives in user metadata; we
 * look at a few likely keys so seeding format doesn't have to be exact.
 */
export function orgTag(metadata: unknown): string | undefined {
  if (!metadata || typeof metadata !== "object") return undefined;
  const m = metadata as Record<string, unknown>;
  const raw =
    (m["@org"] as string) ||
    (m["org"] as string) ||
    (m["orgType"] as string) ||
    (m["type"] as string) ||
    ((m["dbm"] as Record<string, unknown>)?.["org"] as string);
  if (!raw) return undefined;
  const up = String(raw).toUpperCase();
  return ["SUPPLIER", "CUSTOMER", "STAFF"].includes(up) ? up : undefined;
}

/**
 * The label shown under a sender's name — a job title for staff
 * ("Sydney Coordinator"), or "Company · ORG" for a tagged partner
 * ("BC Sands · Supplier"). This is custom metadata, NOT CometChat's
 * built-in group role (owner/admin/member) — those are a different
 * thing and aren't meant for display here.
 */
export function roleLabel(metadata: unknown): { text: string; isPartner: boolean } | undefined {
  if (!metadata || typeof metadata !== "object") return undefined;
  const m = metadata as Record<string, unknown>;
  const org = orgTag(m);
  if (org) {
    const company = m["company"] as string | undefined;
    return { text: company ? `${company} · ${org}` : org, isPartner: true };
  }
  const role = m["role"] as string | undefined;
  if (role) return { text: role, isPartner: false };
  return undefined;
}

/** Escapes a string for safe literal use inside a RegExp. */
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Splits message text on @Mentions into plain strings and mention markers,
 * for the caller to render with distinct styling.
 *
 * `knownNames` (from the message's own getMentionedUsers()) is matched
 * FIRST and greedily — without it a real multi-word mention like
 * "@Jane Doe" would only ever highlight "@Jane", since the fallback
 * pattern can't tell a surname from the next word of the sentence.
 * Longest-first ordering stops "@Jane" shadowing "@Jane Doe".
 */
export function splitMentions(
  text: string,
  knownNames: string[] = []
): Array<{ mention: boolean; text: string }> {
  const parts: Array<{ mention: boolean; text: string }> = [];
  const known = [...new Set(knownNames)].filter(Boolean).sort((a, b) => b.length - a.length);
  const source = known.length
    ? `${known.map((n) => `@${escapeRegExp(n)}`).join("|")}|@[A-Za-z][A-Za-z'-]*`
    : `@[A-Za-z][A-Za-z'-]*`;
  const re = new RegExp(source, "g");
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push({ mention: false, text: text.slice(lastIndex, match.index) });
    parts.push({ mention: true, text: match[0] });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push({ mention: false, text: text.slice(lastIndex) });
  return parts;
}

/**
 * Reads the mentioned-user names off a message, for splitMentions above.
 * Returns [] for messages sent before mentions were wired up — those
 * still get the single-word fallback highlighting.
 */
export function mentionedNames(message: { getMentionedUsers?: () => Array<{ getName(): string }> }): string[] {
  try {
    return (message.getMentionedUsers?.() || []).map((u) => u.getName()).filter(Boolean);
  } catch {
    return [];
  }
}

/** True if `uid` is among the message's mentioned users. */
export function mentionsUid(
  message: { getMentionedUsers?: () => Array<{ getUid(): string }> },
  uid?: string
): boolean {
  if (!uid) return false;
  try {
    return (message.getMentionedUsers?.() || []).some((u) => u.getUid() === uid);
  } catch {
    return false;
  }
}
